import { useRouter } from 'next/router'
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState
} from 'react'
import { useCurrentRefinements, useRefinementList } from 'react-instantsearch'
import { isNotNullOrUndefined } from 'helpers/array'
import {
  AllLevel2Categories,
  raceCategoriesLevel1
} from 'helpers/raceCategoryLevels'
import { recordKeys } from 'helpers/records'

export interface Filter {
  key: AllLevel2Categories
  checked: boolean
}

interface FiltersContext {
  items: ReturnType<typeof useRefinementList>['items']
  filters: Filter[]
  setFilters: Dispatch<SetStateAction<Filter[]>>
  currentRefinementValues: string[]
  applyFilters: () => void
}

export const FiltersContext = createContext<FiltersContext>({
  items: [],
  filters: [],
  setFilters: () => {},
  currentRefinementValues: [],
  applyFilters: () => {}
})

export const FiltersProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter()
  const { query } = router
  const { editionLiveLevel2Category } = query
  const { items, refine } = useRefinementList({
    limit: Infinity,
    attribute: 'editionLiveLevel2CategoryKey',
    operator: 'or'
  })
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: ['editionLiveLevel2CategoryKey']
  })
  const currentRefinementValues = currentRefinements.flatMap(
    ({ refinements }) => refinements.map(({ value }) => String(value))
  )

  const editionLiveLevel2CategoryKey = [editionLiveLevel2Category]
    .filter(isNotNullOrUndefined)
    .flat()

  const [filters, setFilters] = useState(
    recordKeys(raceCategoriesLevel1)
      .flatMap((item) => raceCategoriesLevel1[item].level2Categories)
      .map((item) => ({
        key: item,
        checked: editionLiveLevel2CategoryKey.includes(item)
      }))
  )

  useEffect(() => {
    const wantedValues = editionLiveLevel2CategoryKey
      .filter((value) => !currentRefinementValues.includes(value))
      .concat(
        currentRefinementValues.filter(
          (value) => !editionLiveLevel2CategoryKey.includes(value)
        )
      )
    wantedValues.forEach(refine)
    setFilters((prev) =>
      prev.map((filter) => ({
        ...filter,
        checked: editionLiveLevel2CategoryKey.includes(filter.key)
      }))
    )
  }, [editionLiveLevel2CategoryKey.join()])

  const applyFilters = () => {
    const selectedFilters = filters.filter((item) => item.checked)
    const selectedValues = selectedFilters.map((item) => item.key)
    const wantedValues = selectedValues.filter(
      (value) => !currentRefinementValues.includes(value)
    )
    const unwantedValues = currentRefinementValues.filter(
      (value) => !selectedValues.includes(value as AllLevel2Categories)
    )
    ;[...wantedValues, ...unwantedValues].forEach(refine)
  }
  const value = {
    items,
    filters,
    setFilters,
    currentRefinementValues,
    applyFilters
  }
  return (
    <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>
  )
}
